import React, { useEffect, useMemo, useRef, useState } from "react";
import "../styles/Kitab.css";
import { collection, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { dbAdmin } from "../firebaseConfig";
import { getHariLong, getTarikh, capitalizeWords } from "../utils/formatHelpers";
import { expandRecurringRules, extractKitabId, toYMD } from "../utils/recurringUtils";

const getStartMs = (k) => {
    if (!k) return 0;
    if (k.start?.seconds) return k.start.seconds * 1000;
    if (typeof k.start?.toDate === "function") return k.start.toDate().getTime();
    if (k.start instanceof Date) return k.start.getTime();
    if (typeof k.start === "number") return k.start;
    return 0;
};

const formatTime = (ms) => {
    try {
        return new Date(ms)
            .toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
            })
            .toLowerCase();
    } catch {
        return "";
    }
};

// helper ambil gambar/jawatan ustaz dari event (support field lama+baru)
const getSpeakerImage = (s) => s?.speakerImage || s?.gambar_ustaz || "";
const getSpeakerPosition = (s) => s?.speakerPosition || s?.position_ustaz || "";

const KitabView = ({ kitab, v2Masjids = [], onClose }) => {
    const [showAll, setShowAll] = useState(false);

    // data sources
    const [subSingles, setSubSingles] = useState([]); // from /masajid/{id}/kuliah
    const [subRecurring, setSubRecurring] = useState([]); // expanded from /kuliahRecurring

    // keep local mutable store per-masjid (supaya update snapshot tak ganggu masjid lain)
    const singlesByMasjidRef = useRef(new Map()); // masjidId -> [events]
    const recurringByMasjidRef = useRef(new Map()); // masjidId -> [expanded events]

    const kitabTitle = kitab?.title || kitab?.name || kitab?.id || "Kitab";
    const kitabCover = kitab?.coverImage || "";
    const kitabCategory = kitab?.category || "";
    const kitabAuthor = kitab?.author || "";
    const kitabDesc = kitab?.description || kitab?.desc || "";

    const kitabId = useMemo(() => String(kitab?.id || ""), [kitab?.id]);
    const sourceMasjids = v2Masjids;

    // ✅ nowMs stabil (jangan letak Date.now() dalam deps useMemo)
    const todayYMD = useMemo(() => toYMD(new Date()), []);

    // map masjidId -> exception set (kalau kau simpan exdate dalam masjid doc)
    const exSetByMasjid = useMemo(() => {
        const map = new Map();
        (Array.isArray(sourceMasjids) ? sourceMasjids : []).forEach((m) => {
            const masjidId = m?.id;
            if (!masjidId) return;

            const exList = m?.aktiviti?.kuliahRecurringExceptions || [];
            const exSet = new Set(
                exList
                    .filter((x) => x?.seriesId && x?.dateYMD)
                    .map((x) => `${x.seriesId}__${x.dateYMD}`)
            );
            map.set(masjidId, exSet);
        });
        return map;
    }, [sourceMasjids]);

    // =========================
    // ✅ Subscribe subcollection kuliah + kuliahRecurring (KITAB FILTER)
    // =========================
    useEffect(() => {
        if (!kitabId) {
            setSubSingles([]);
            setSubRecurring([]);
            singlesByMasjidRef.current = new Map();
            recurringByMasjidRef.current = new Map();
            return;
        }

        const masjidIds = (Array.isArray(sourceMasjids) ? sourceMasjids : [])
            .map((m) => m?.id)
            .filter(Boolean);

        if (masjidIds.length === 0) {
            setSubSingles([]);
            setSubRecurring([]);
            singlesByMasjidRef.current = new Map();
            recurringByMasjidRef.current = new Map();
            return;
        }

        const unsubs = [];

        const rebuildSingles = () => {
            const all = [];
            singlesByMasjidRef.current.forEach((arr) => arr.forEach((x) => all.push(x)));
            setSubSingles(all);
        };

        const rebuildRecurring = () => {
            const all = [];
            recurringByMasjidRef.current.forEach((arr) => arr.forEach((x) => all.push(x)));
            setSubRecurring(all);
        };

        masjidIds.forEach((masjidId) => {
            // ---- 1) Singles: /masajid/{id}/kuliah (kitabId + start upcoming)
            // Nota: query ni mungkin perlukan composite index (kitabId + start)
            const qSingles = query(
                collection(dbAdmin, "masajid", masjidId, "kuliah"),
                where("kitabId", "==", kitabId),
                where("start", ">", new Date()),
                orderBy("start", "asc"),
                limit(80)
            );

            unsubs.push(
                onSnapshot(
                    qSingles,
                    (snap) => {
                        const rows = snap.docs
                            .map((d) => {
                                const data = d.data() || {};
                                const start = data.start?.toDate
                                    ? data.start.toDate()
                                    : data.start?.seconds
                                        ? new Date(data.start.seconds * 1000)
                                        : data.start instanceof Date
                                            ? data.start
                                            : null;

                                if (!start) return null;

                                return {
                                    ...data,
                                    masjidId,
                                    eventId: data.eventId || d.id,
                                    start,
                                    __type: "single",
                                };
                            })
                            .filter(Boolean);

                        singlesByMasjidRef.current.set(masjidId, rows);
                        rebuildSingles();
                    },
                    (err) => console.error("❌ KitabView singles error:", err)
                )
            );

            // ---- 2) Recurring: /masajid/{id}/kuliahRecurring -> expand
            const qRec = query(
                collection(dbAdmin, "masajid", masjidId, "kuliahRecurring"),
                where("repeatEnd", ">=", todayYMD),
                orderBy("repeatEnd", "asc"),
                limit(250)
            );

            unsubs.push(
                onSnapshot(
                    qRec,
                    (snap) => {
                        const rules = snap.docs
                            .map((d) => {
                                const r = d.data() || {};
                                return { ...r, masjidId, seriesId: r.seriesId || d.id };
                            })
                            // ✅ FILTER KITAB (support pelbagai field)
                            .filter((r) => String(extractKitabId(r) || "") === kitabId);

                        // expand (lagi jauh sikit supaya kitab page nampak jauh)
                        const WINDOW_DAYS = 365;
                        const expandedRaw = expandRecurringRules(rules, masjidId, Date.now(), WINDOW_DAYS);

                        // apply EXDATE (kalau ada)
                        const exSet = exSetByMasjid.get(masjidId) || new Set();
                        const expanded = expandedRaw.filter((ev) => {
                            const ymd = toYMD(ev.start);
                            const key = `${ev.seriesId || ev.seriesKey}__${ymd}`;
                            return !exSet.has(key);
                        });

                        recurringByMasjidRef.current.set(
                            masjidId,
                            expanded.map((ev) => ({ ...ev, __type: "recurring" }))
                        );
                        rebuildRecurring();
                    },
                    (err) => console.error("❌ KitabView recurring error:", err)
                )
            );
        });

        return () => unsubs.forEach((fn) => fn && fn());
    }, [kitabId, sourceMasjids, todayYMD, exSetByMasjid]);

    // =========================
    // ✅ Merge: legacy + singles + recurring
    // =========================
    const { sessions, grouped } = useMemo(() => {
        const nowMs = Date.now();

        // legacy (data lama dalam doc masjid)
        const legacy = (Array.isArray(sourceMasjids) ? sourceMasjids : []).flatMap((m) => {
            const masjidId = m?.id;
            const list = m?.aktiviti?.kuliah || [];
            const masjidName = capitalizeWords(m?.profail?.nama || m?.nama || masjidId || "Masjid");
            const masjidLogo = m?.profail?.logo || m?.profail?.logoJa || m?.profail?.gambar || m?.logo || m?.gambar || "";

            return (Array.isArray(list) ? list : [])
                .filter((k) => String(k?.kitabId || "") === kitabId)
                .map((k) => {
                    const ms = getStartMs(k);
                    const start = ms ? new Date(ms) : null;
                    if (!start) return null;

                    return {
                        ...k,
                        masjidId,
                        masjidName,
                        masjidLogo,
                        start,
                        eventId: k.eventId || `${masjidId}-${ms}`,
                        __type: "legacy",
                    };
                })
                .filter(Boolean);
        });

        // singles + recurring dah ada start as Date
        const normalize = (arr) =>
            (Array.isArray(arr) ? arr : [])
                .map((k) => {
                    const ms = getStartMs(k) || (k.start instanceof Date ? k.start.getTime() : 0);
                    if (!ms) return null;

                    // cari masjid meta
                    const m = (Array.isArray(sourceMasjids) ? sourceMasjids : []).find((x) => x?.id === k.masjidId);
                    const masjidName = capitalizeWords(m?.profail?.nama || m?.nama || k.masjidId || "Masjid");
                    const masjidLogo = m?.profail?.logo || m?.profail?.logoJa || m?.profail?.gambar || m?.logo || m?.gambar || "";

                    return {
                        ...k,
                        ms,
                        masjidName,
                        masjidLogo,

                        // normalize penceramah
                        speakerText: k.speaker || k.nama_ustaz || "",
                        speakerImg: getSpeakerImage(k),
                        speakerPos: getSpeakerPosition(k),

                        // normalize program
                        programText: k.program || k.title || "Kuliah",
                    };
                })
                .filter(Boolean);

        const mergedRaw = [
            ...normalize(legacy),
            ...normalize(subSingles),
            ...normalize(subRecurring),
        ];

        // dedupe (elak legacy + new double)
        const map = new Map();
        const put = (k) => {
            const ms = k.ms || 0;
            if (!ms) return;

            const key = k.eventId
                ? `${k.masjidId}|${k.eventId}`
                : `${k.masjidId}|${ms}|${k.programText || ""}|${k.speakerText || ""}`;

            if (!map.has(key)) map.set(key, k);
        };
        mergedRaw.forEach(put);

        const upcoming = Array.from(map.values())
            .filter((s) => (s.ms || 0) > nowMs)
            .sort((a, b) => (a.ms || 0) - (b.ms || 0));

        // group by masjid
        const gmap = new Map();
        for (const s of upcoming) {
            if (!gmap.has(s.masjidId)) gmap.set(s.masjidId, []);
            gmap.get(s.masjidId).push(s);
        }

        const groupedArr = Array.from(gmap.entries()).map(([masjidId, items]) => {
            const first = items[0];
            return {
                masjidId,
                masjidName: first.masjidName,
                masjidLogo: first.masjidLogo,
                items,
            };
        });

        groupedArr.sort((a, b) => (a.items[0]?.ms || 0) - (b.items[0]?.ms || 0));

        return { sessions: upcoming, grouped: groupedArr };
    }, [kitabId, sourceMasjids, subSingles, subRecurring,]);

    const sessionsToShow = showAll ? sessions : sessions.slice(0, 12);

    return (
        <div className="kitab-view">
            {/* Header: Tutup kanan atas */}
            <div className="kitab-view-topbar">
                <div />
                <button className="kitab-close-top" onClick={onClose} type="button">
                    ✕ Tutup
                </button>
            </div>

            <div className="kitab-view-header">
                <div className="kitab-head">
                    {kitabCover ? (
                        <img className="kitab-head-cover" src={kitabCover} alt={kitabTitle} />
                    ) : (
                        <div className="kitab-head-cover fallback" />
                    )}

                    <div className="kitab-head-text">
                        <div className="kitab-head-title">{kitabTitle}</div>
                        <div className="kitab-head-meta">
                            {kitabCategory || "Umum"}
                            {kitabAuthor ? ` • ${kitabAuthor}` : ""}
                        </div>
                    </div>
                </div>
            </div>

            {kitabDesc ? <div className="kitab-desc">{kitabDesc}</div> : null}

            <div className="kitab-section-title">Masjid yang mengajar kitab ini</div>

            {grouped.length === 0 ? (
                <div className="kitab-empty">Tiada jadual kuliah untuk kitab ini lagi.</div>
            ) : (
                <div className="kitab-masjid-grid">
                    {grouped.map((g) => (
                        <div className="kitab-masjid-card" key={g.masjidId}>
                            <div className="kitab-masjid-head">
                                {g.masjidLogo ? (
                                    <img
                                        className="kitab-masjid-logo"
                                        src={g.masjidLogo}
                                        alt={g.masjidName}
                                        onError={(e) => (e.currentTarget.style.display = "none")}
                                    />
                                ) : (
                                    <div className="kitab-masjid-logo fallback" />
                                )}
                                <div className="kitab-masjid-name">{g.masjidName}</div>
                            </div>

                            <div className="kitab-masjid-list">
                                {g.items.slice(0, 3).map((s, i) => (
                                    <div className="kitab-session-row" key={i}>
                                        <div className="kitab-session-date">
                                            {`${getHariLong(new Date(s.ms))}, ${getTarikh(new Date(s.ms))}`}
                                        </div>

                                        <div className="kitab-session-time">{formatTime(s.ms)}</div>

                                        <div className="kitab-session-speaker">
                                            {s.speakerImg ? (
                                                <img
                                                    className="kitab-speaker-avatar"
                                                    src={s.speakerImg}
                                                    alt={s.speakerText || "Penceramah"}
                                                    onError={(e) => (e.currentTarget.style.display = "none")}
                                                />
                                            ) : (
                                                <div className="kitab-speaker-avatar fallback" />
                                            )}

                                            <div className="kitab-speaker-text">
                                                <div className="kitab-speaker-name">{s.speakerText}</div>
                                                {!!s.speakerPos && <div className="kitab-speaker-pos">{s.speakerPos}</div>}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="kitab-section-title">Senarai kuliah akan datang</div>

            <div className="kitab-sessions">
                {sessionsToShow.map((s, idx) => (
                    <div className="kitab-session-card" key={`${s.eventId || idx}`}>
                        <div className="kitab-session-left">
                            <div className="kitab-session-date">
                                {`${getHariLong(new Date(s.ms))}, ${getTarikh(new Date(s.ms))}`}
                            </div>
                            <div className="kitab-session-sub">{s.programText}</div>
                            <div className="kitab-session-masjid">{s.masjidName}</div>
                        </div>

                        <div className="kitab-session-right">
                            <div className="kitab-session-time big">{formatTime(s.ms)}</div>
                            <div className="kitab-session-ustaz">{s.speakerText}</div>
                            {!!s.speakerPos && <div className="kitab-session-pos">{s.speakerPos}</div>}
                        </div>
                    </div>
                ))}
            </div>

            {sessions.length > 12 && (
                <div className="kitab-more">
                    <button className="kitab-more-btn" onClick={() => setShowAll((p) => !p)} type="button">
                        {showAll ? "Tutup senarai" : "Lihat semua"}
                    </button>
                </div>
            )}
        </div>
    );
};

export default KitabView;
